/**
 * Insurance Centre — contractor side. Lists the two mandatory insurances
 * (Public Liability + Personal Accident / Income Protection), points the
 * sub at the partner broker to buy, then takes back the Certificate of
 * Currency PDF / image and tracks expiry.
 *
 * Spec lives in ACTS_SEGUROS_APP_SPEC.md. UI here re-uses the app's design
 * system (Inter via index.css :root, navy/yellow palette) — the prototype's
 * standalone CSS is intentionally NOT copied.
 *
 * Mobile-first: contractors will hit this from their phone, so the layout
 * is single-column with generous tap targets.
 */

import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

// Brand tokens that match the rest of the app.
const NAVY    = '#1e3a4a';
const NAVY_LT = '#4A7A91';
const YELLOW  = '#F5C518';
const TEXT    = '#1A1A1A';
const MUTED   = '#6b7280';
const SUBTLE  = '#9ca3af';
const LINE    = '#e5e7eb';
const SURFACE = '#ffffff';
const BG      = '#F7F7F2';

// Status pill styling — one source of truth.
const STATUS_META = {
  pending:  { label: 'Pending',     bg: '#eef2f6', fg: '#5b6b7b' },
  valid:    { label: 'Valid',       bg: '#dcfce7', fg: '#15803d' },
  expiring: { label: 'Expiring',    bg: '#fef3c7', fg: '#92400e' },
  expired:  { label: 'Expired',     bg: '#fee2e2', fg: '#991b1b' },
};

const TIPO_META = {
  pl: {
    label:    'Public Liability',
    pt:       'Responsabilidade Civil',
    icon:     '🛡️',
    helpSteps: [
      'Ocupação: <strong>Car Detailer / Car wash</strong> se lava/detalha carros (a maioria de vocês).',
      'Ocupação: <strong>Cleaning / Cleaner</strong> se faz limpeza de escritório.',
      'Faz os dois? Declare <strong>as duas atividades</strong> na mesma apólice.',
      'Tipo de negócio: <strong>Sole trader</strong>.',
      'Cobertura: <strong>$20M obrigatório</strong> — sem outra opção.',
    ],
    warning: 'O seguro só cobre a atividade que você declarar. Se detalha carros mas declara só "cleaning", um dano num carro pode não ser coberto.',
    priceHint: 'Referência (sole trader, $20M): office cleaner ~$55–$70/mês · car detailer ~$75–$120/mês.',
  },
  ip: {
    label:    'Personal Accident / Income Protection',
    pt:       'Acidentes Pessoais / Proteção de Renda',
    icon:     '🚑',
    helpSteps: [
      'Adicione <strong>Personal Accident &amp; Illness</strong> na cotação.',
      'Benefício semanal — <strong>fixo da Acts</strong>:',
      '• Office cleaner → <strong>$500/semana</strong>',
      '• Car detailer  → <strong>$1.000/semana</strong>',
      'Período de espera (waiting period) curto se possível.',
    ],
    warning: 'Como você é contratado, não tem workers compensation — esse seguro é sua rede se você se machucar.',
    priceHint: 'Referência: ~$30–$80/mês conforme idade e ocupação.',
  },
};

function fmtDateDmy(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}
function fmtBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function InsuranceCenter({ user }) {
  const [config, setConfig]               = useState(null);
  const [certificates, setCertificates]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [uploadTipo, setUploadTipo]       = useState(null); // 'pl' | 'ip' | null
  const [expandedHelp, setExpandedHelp]   = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [cfg, mine] = await Promise.all([
        api.insuranceConfig(),
        api.myInsuranceCertificates(),
      ]);
      setConfig(cfg);
      setCertificates(mine);
    } catch (err) {
      console.error('Insurance load failed:', err);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  // Latest certificate per tipo (rows come back ordered tipo, newest-first).
  function latestFor(tipo) {
    return certificates.find(c => c.tipo === tipo) || null;
  }
  function statusFor(tipo) {
    const latest = latestFor(tipo);
    return latest ? latest.status : 'pending';
  }

  const helpHref = config?.help_whatsapp
    ? `https://wa.me/${String(config.help_whatsapp).replace(/[^0-9]/g, '')}`
    : 'tel:+61494304689';

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '20px 16px 60px' }}>

      {/* Hero */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 18 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{
            margin: 0, fontSize: 20, fontWeight: 800, color: NAVY, letterSpacing: -0.3,
          }}>Central de Seguros</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13.5, color: MUTED, lineHeight: 1.5 }}>
            Dois seguros obrigatórios pra trabalhar com a Acts. Contrate no parceiro,
            depois envie o certificado aqui.
          </p>
        </div>
      </div>

      {/* 3-step strip */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 22,
      }}>
        {[
          { n: 1, t: 'Contrate no site do parceiro' },
          { n: 2, t: 'Baixe o Certificate of Currency' },
          { n: 3, t: 'Envie o certificado aqui' },
        ].map(s => (
          <div key={s.n} style={{
            background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 12,
            padding: '12px 10px', textAlign: 'center',
          }}>
            <div style={{
              display: 'inline-flex', width: 24, height: 24, borderRadius: '50%',
              background: NAVY, color: '#fff', fontSize: 12, fontWeight: 700,
              alignItems: 'center', justifyContent: 'center', marginBottom: 6,
            }}>{s.n}</div>
            <p style={{ margin: 0, fontSize: 11.5, color: MUTED, lineHeight: 1.35 }}>{s.t}</p>
          </div>
        ))}
      </div>

      {/* Help button */}
      <a href={helpHref} target="_blank" rel="noreferrer"
         style={{
           display: 'block', textAlign: 'center', padding: '10px 14px',
           background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 10,
           color: NAVY, textDecoration: 'none', fontWeight: 700, fontSize: 13,
           marginBottom: 18,
         }}>
        💬 Falar com a Acts
      </a>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: MUTED, fontSize: 13 }}>
          Carregando…
        </div>
      ) : (
        <>
          {/* The 2 insurance cards */}
          {(['pl', 'ip']).map(tipo => {
            const meta    = TIPO_META[tipo];
            const status  = statusFor(tipo);
            const latest  = latestFor(tipo);
            const brokerUrl = tipo === 'pl' ? config?.broker_pl_url : config?.broker_ip_url;
            const helpOpen = expandedHelp === tipo;
            const sMeta    = STATUS_META[status];

            return (
              <div key={tipo} style={{
                background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 14,
                boxShadow: '0 1px 2px rgba(31,58,95,0.06), 0 8px 24px rgba(31,58,95,0.05)',
                padding: 16, marginBottom: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{
                    flex: 'none', width: 44, height: 44, borderRadius: 12,
                    background: '#eaf0f6', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 24,
                  }}>{meta.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ margin: 0, fontSize: 16, color: NAVY, fontWeight: 700 }}>
                      {meta.pt}
                    </h3>
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{meta.label}</div>
                  </div>
                  <span style={{
                    flex: 'none', fontSize: 11.5, fontWeight: 700, padding: '4px 10px',
                    borderRadius: 999, whiteSpace: 'nowrap',
                    background: sMeta.bg, color: sMeta.fg,
                  }}>{sMeta.label}</span>
                </div>

                {/* What is it */}
                <p style={{ margin: '12px 0 0', fontSize: 13.5, color: TEXT, lineHeight: 1.5 }}>
                  {tipo === 'pl'
                    ? 'Cobre danos a terceiros enquanto você trabalha — riscar/danificar um carro, ou alguém escorregar no piso.'
                    : 'Cobre você. Se você se machucar e ficar sem trabalhar, esse seguro paga um valor semanal.'}
                </p>

                {/* Latest metadata if any */}
                {latest && (
                  <div style={{
                    marginTop: 12, borderTop: `1px dashed ${LINE}`, paddingTop: 10,
                    fontSize: 12.5, color: MUTED,
                  }}>
                    <div>Seguradora: <strong style={{ color: TEXT }}>{latest.seguradora}</strong></div>
                    <div>Apólice: <strong style={{ color: TEXT, fontFamily: 'monospace' }}>{latest.numero_apolice}</strong></div>
                    <div>Validade: <strong style={{ color: TEXT }}>{fmtDateDmy(latest.validade)}</strong></div>
                    <a href={api.insuranceFileUrl(latest.id)} target="_blank" rel="noreferrer"
                       style={{ color: NAVY_LT, fontWeight: 600, fontSize: 12, textDecoration: 'underline' }}>
                      Abrir certificado ({latest.arquivo_name})
                    </a>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                  <a href={brokerUrl || '#'} target="_blank" rel="noreferrer"
                     style={{
                       flex: '1 1 160px', textAlign: 'center', padding: '12px 14px',
                       borderRadius: 11, background: NAVY, color: '#fff', fontWeight: 700,
                       fontSize: 14, textDecoration: 'none', pointerEvents: brokerUrl ? 'auto' : 'none',
                       opacity: brokerUrl ? 1 : 0.5,
                     }}>
                    Contratar agora
                  </a>
                  <button onClick={() => setUploadTipo(tipo)}
                          style={{
                            flex: '1 1 160px', padding: '12px 14px', borderRadius: 11,
                            background: '#fff', color: NAVY, fontWeight: 700, fontSize: 14,
                            border: `1.5px solid ${LINE}`, cursor: 'pointer',
                          }}>
                    📎 Enviar certificado
                  </button>
                </div>

                {/* Help expander */}
                <button onClick={() => setExpandedHelp(helpOpen ? null : tipo)}
                        style={{
                          marginTop: 10, width: '100%', padding: '8px 10px', borderRadius: 8,
                          background: 'transparent', border: `1px solid ${LINE}`,
                          color: NAVY, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          textAlign: 'left', display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                  <span>Como contratar + quanto custa</span>
                  <span style={{ fontSize: 14 }}>{helpOpen ? '▲' : '▼'}</span>
                </button>
                {helpOpen && (
                  <div style={{ marginTop: 8, fontSize: 12.5, color: TEXT, lineHeight: 1.55 }}>
                    <ul style={{ paddingLeft: 18, margin: '6px 0' }}>
                      {meta.helpSteps.map((s, i) => (
                        <li key={i} style={{ marginBottom: 4 }}
                            dangerouslySetInnerHTML={{ __html: s }} />
                      ))}
                    </ul>
                    <div style={{
                      background: '#FFFBEB', border: '1px solid #FDE68A', padding: 10,
                      borderRadius: 8, color: '#92400E', fontSize: 12,
                    }}>⚠ {meta.warning}</div>
                    <div style={{ marginTop: 8, color: MUTED, fontSize: 12 }}>
                      💲 {meta.priceHint}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* History — all uploads, newest first */}
          <h3 style={{
            margin: '24px 0 10px', fontSize: 13, fontWeight: 800, color: NAVY_LT,
            letterSpacing: 0.6, textTransform: 'uppercase',
          }}>Histórico de envios</h3>
          {certificates.length === 0 ? (
            <div style={{
              background: SURFACE, border: `1px dashed ${LINE}`, borderRadius: 14,
              padding: 22, textAlign: 'center', color: MUTED, fontSize: 13.5,
            }}>
              Nenhum certificado enviado ainda.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {certificates.map(c => {
                const sMeta = STATUS_META[c.status] || STATUS_META.pending;
                return (
                  <div key={c.id} style={{
                    background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 10,
                    padding: 12, display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <div style={{ fontSize: 20 }}>{TIPO_META[c.tipo]?.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>
                        {TIPO_META[c.tipo]?.pt}
                      </div>
                      <div style={{ fontSize: 11.5, color: MUTED }}>
                        {c.seguradora} · {c.numero_apolice} · validade {fmtDateDmy(c.validade)}
                      </div>
                      <a href={api.insuranceFileUrl(c.id)} target="_blank" rel="noreferrer"
                         style={{ fontSize: 11, color: NAVY_LT, fontWeight: 600 }}>
                        {c.arquivo_name} ({fmtBytes(c.arquivo_size)})
                      </a>
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999,
                      background: sMeta.bg, color: sMeta.fg, whiteSpace: 'nowrap',
                    }}>{sMeta.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Upload modal */}
      {uploadTipo && (
        <UploadModal
          tipo={uploadTipo}
          onClose={() => setUploadTipo(null)}
          onUploaded={() => { setUploadTipo(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Upload sheet ────────────────────────────────────────────────────────────
// Form for one insurance type. Validates client-side then base64-encodes the
// file into the JSON body — keeps the API surface to a single POST.
function UploadModal({ tipo, onClose, onUploaded }) {
  const fileRef    = useRef(null);
  const [seguradora,     setSeguradora]     = useState('');
  const [numeroApolice,  setNumeroApolice]  = useState('');
  const [validade,       setValidade]       = useState('');
  const [file,           setFile]           = useState(null);
  const [busy,           setBusy]           = useState(false);
  const [error,          setError]          = useState('');

  const meta = TIPO_META[tipo];
  const ACCEPT     = 'application/pdf,image/jpeg,image/png,image/jpg';
  const MAX_BYTES  = 10 * 1024 * 1024;

  function pickFile(e) {
    setError('');
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'].includes(f.type)) {
      setError('Apenas PDF, JPG ou PNG.');
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(`Arquivo muito grande (${(f.size / 1024 / 1024).toFixed(1)} MB; máx 10 MB).`);
      return;
    }
    setFile(f);
  }

  // FileReader → base64 (strip data URL prefix that comes by default).
  function readBase64(f) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => {
        const r = String(reader.result || '');
        const i = r.indexOf(',');
        resolve(i >= 0 ? r.slice(i + 1) : r);
      };
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });
  }

  async function submit() {
    setError('');
    if (!seguradora.trim())    return setError('Informe a seguradora.');
    if (!numeroApolice.trim()) return setError('Informe o número da apólice.');
    if (!validade)             return setError('Informe a validade.');
    if (!file)                 return setError('Anexe o certificado.');
    setBusy(true);
    try {
      const b64 = await readBase64(file);
      await api.uploadInsuranceCertificate({
        tipo,
        seguradora:     seguradora.trim(),
        numero_apolice: numeroApolice.trim(),
        validade,
        file_name:      file.name,
        file_mime:      file.type,
        file_base64:    b64,
      });
      onUploaded();
    } catch (err) {
      setError(err.message || 'Falha ao enviar.');
      setBusy(false);
    }
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
        padding: 22, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, color: NAVY, fontWeight: 800 }}>
          Enviar certificado
        </h3>
        <div style={{ fontSize: 12, color: MUTED, marginBottom: 14 }}>{meta.pt}</div>

        <label style={fieldLabel}>Seguradora</label>
        <input value={seguradora} onChange={e => setSeguradora(e.target.value)}
               placeholder="Ex.: BizCover" style={inputStyle} />

        <label style={fieldLabel}>Número da apólice</label>
        <input value={numeroApolice} onChange={e => setNumeroApolice(e.target.value)}
               placeholder="Ex.: BC-123456" style={inputStyle} />

        <label style={fieldLabel}>Validade</label>
        <input type="date" value={validade} onChange={e => setValidade(e.target.value)}
               style={inputStyle} />

        <label style={fieldLabel}>Certificado (PDF, JPG ou PNG · máx 10 MB)</label>
        <div onClick={() => fileRef.current?.click()} style={{
          padding: '14px 12px', border: `1.5px dashed ${LINE}`, borderRadius: 10,
          textAlign: 'center', cursor: 'pointer', background: BG,
          color: file ? TEXT : MUTED, fontSize: 13,
        }}>
          {file ? (
            <span><strong>{file.name}</strong> ({fmtBytes(file.size)})</span>
          ) : (
            <span>Toque para anexar o Certificate of Currency</span>
          )}
          <input ref={fileRef} type="file" accept={ACCEPT}
                 style={{ display: 'none' }} onChange={pickFile} />
        </div>

        {error && (
          <div style={{
            marginTop: 12, padding: '8px 10px', background: '#FEE2E2',
            border: '1px solid #FCA5A5', borderRadius: 8, color: '#991B1B', fontSize: 12.5,
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy} style={btnGhost}>Cancelar</button>
          <button onClick={submit}  disabled={busy} style={btnPrimary}>
            {busy ? 'Enviando…' : 'Enviar para a Acts'}
          </button>
        </div>
      </div>
    </div>
  );
}

const fieldLabel = {
  display: 'block', fontSize: 11.5, fontWeight: 700, color: MUTED,
  textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 12, marginBottom: 4,
};
const inputStyle = {
  width: '100%', padding: '9px 11px', border: `1px solid ${LINE}`, borderRadius: 8,
  fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit',
};
const btnGhost   = {
  padding: '9px 16px', borderRadius: 10, border: `1px solid ${LINE}`,
  background: '#fff', color: NAVY, fontWeight: 600, fontSize: 13, cursor: 'pointer',
};
const btnPrimary = {
  padding: '9px 18px', borderRadius: 10, border: 'none',
  background: NAVY, color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer',
};
